import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import { Helmet } from 'react-helmet-async';
import { services, partners, faqs, siteConfig, strategicCustomers } from '@/data/siteData';
import { 
  ArrowRight, 
  Monitor, 
  Code, 
  Cloud, 
  Shield, 
  CheckCircle2,
  ChevronDown,
  Server,
  Headphones
} from 'lucide-react';

const iconMap = {
  compute: Monitor,
  network: Shield,
  monitoring: Server,
  services: Code,
};

const servicesList = Object.values(services);

// Service Card Component
const ServiceCard = ({ service, index }) => {
  const IconComponent = iconMap[service.id] || Monitor;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ delay: index * 0.1 }}
    >
      <Link 
        to={`/services/${service.id}`}
        className="group block bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#0066CC]/20 transition-all duration-300"
      >
        <div className="w-14 h-14 rounded-xl bg-[#0066CC]/10 flex items-center justify-center mb-5 group-hover:bg-[#0066CC] transition-colors">
          <IconComponent className="w-7 h-7 text-[#0066CC] group-hover:text-white transition-colors" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#0066CC] transition-colors">
          {service.title}
        </h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {service.description}
        </p>
        <span className="inline-flex items-center gap-1 text-[#0066CC] font-medium text-sm group-hover:gap-2 transition-all">
          Learn more <ArrowRight className="w-4 h-4" />
        </span>
      </Link>
    </motion.div>
  );
};

// FAQ Item Component
const FAQItem = ({ faq, isOpen, toggle, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.05 }}
    className="border-b border-gray-200"
  >
    <button
      onClick={toggle}
      className="w-full flex items-center justify-between py-5 text-left group"
    >
      <span className="font-medium text-gray-900 group-hover:text-[#0066CC] transition-colors pr-8">
        {faq.question}
      </span>
      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <p className="pb-5 text-gray-600 text-sm leading-relaxed">
        {faq.answer}
      </p>
    </motion.div>
  </motion.div>
);

export const HomePage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Felix Infotech - Your Trusted Partner for Innovative IT Solutions | 22+ Years</title>
        <meta name="description" content="Felix Infotech enables secure, scalable, and intelligent IT ecosystems with 22+ years of excellence. Compute, Network & Security, Monitoring, and Professional Services across India, Dubai & Singapore." />
        <meta name="keywords" content="IT solutions India, cybersecurity, network security, cloud computing, data center, managed services, VAPT, SOC monitoring, system integrator Chennai, enterprise IT services, Felix Infotech" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/" />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Felix Infotech - Your Trusted Partner for Innovative IT Solutions" />
        <meta property="og:description" content="22+ years of IT transformation excellence. Compute, Network & Security, Monitoring, and Professional Services across India, Dubai & Singapore." />
        <meta property="og:url" content="https://felixinfotech.com/" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta property="og:image" content="https://felixinfotech.com/img/felix-og.png" />
        <meta property="og:locale" content="en_IN" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Felix Infotech - Innovative IT Solutions | 22+ Years" />
        <meta name="twitter:description" content="Enabling secure, scalable IT ecosystems. Compute, Network & Security, Monitoring, and Professional Services." />
        <meta name="twitter:image" content="https://felixinfotech.com/img/felix-og.png" />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Felix Infotech Pvt Ltd",
            "url": "https://felixinfotech.com",
            "logo": "https://felixinfotech.com/img/felix-logo.png",
            "description": "Premier system integrator and managed services provider with 22+ years of IT transformation excellence.",
            "foundingDate": "2002",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Old No.11, New No.28, Mc Nichols Road, 3rd Lane, Chetpet",
              "addressLocality": "Chennai",
              "addressRegion": "Tamil Nadu",
              "postalCode": "600031",
              "addressCountry": "IN"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-44-48645444",
              "contactType": "customer service",
              "areaServed": ["IN", "AE", "SG"],
              "availableLanguage": ["English", "Tamil", "Hindi"]
            },
            "sameAs": [
              "https://www.linkedin.com/company/felix-infotech",
              "https://www.facebook.com/felixinfotech"
            ],
            "numberOfEmployees": {
              "@type": "QuantitativeValue",
              "value": "100+"
            },
            "areaServed": ["India", "Dubai", "Singapore"]
          }
        `}</script>
      </Helmet>

      <ScrollProgress />
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="bg-white pt-16 pb-8 lg:pt-24 lg:pb-12">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs font-semibold tracking-widest uppercase text-[#0066CC] mb-4"
              >
                22+ Years of IT Transformation Excellence
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
              >
                Your Trusted Partner for
                <br />
                <span className="text-[#0066CC]">Innovative IT Solutions</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-lg text-gray-600 max-w-3xl mx-auto mb-10"
              >
                Enabling secure, scalable, and intelligent IT ecosystems. Felix Infotech delivers 
                end-to-end IT solutions across Compute, Network & Security, Monitoring, and Professional Services 
                to enterprises across India, Dubai, and Singapore.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12"
              >
                <Link 
                  to="/services" 
                  className="inline-flex items-center px-8 py-4 bg-[#0066CC] text-white font-semibold rounded-full hover:bg-[#0055AA] transition-colors shadow-lg"
                >
                  Explore Our Solutions
                  <ArrowRight className="w-5 h-5 ml-2 text-white" />
                </Link>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center px-8 py-4 border-2 border-[#0066CC] text-[#0066CC] font-semibold rounded-full hover:bg-[#0066CC] hover:text-white transition-colors"
                >
                  <Headphones className="w-5 h-5 mr-2" />
                  Get Free Consultation
                </Link>
              </motion.div>

              {/* Stats Row */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
              >
                {[
                  { value: '22+', label: 'Years of Excellence' },
                  { value: '100+', label: 'Certified Experts' },
                  { value: '5%', label: 'CAGR Growth' },
                  { value: '₹100Cr+', label: 'Annual Revenue' },
                ].map((stat, index) => (
                  <div key={stat.label} className="text-center">
                    <p className="text-3xl lg:text-4xl font-bold text-[#0066CC]">{stat.value}</p>
                    <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* 4 Solutions - Services Grid */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-[#0066CC] mb-3">Our Solutions</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                End-to-End IT Solutions
                <br />
                <span className="text-[#0066CC]">For Modern Enterprises</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Comprehensive technology solutions spanning compute infrastructure, network security, 
                IT monitoring, and professional services to drive your digital transformation.
              </p>
            </motion.div>

            {/* 4 Services Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {servicesList.map((service, index) => (
                <ServiceCard key={service.id} service={service} index={index} />
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Link 
                to="/services" 
                className="inline-flex items-center px-6 py-3 border-2 border-[#0066CC] text-[#0066CC] font-medium rounded-full hover:bg-[#0066CC] hover:text-white transition-colors"
              >
                View All Solutions
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Strategic Customers */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-[#0066CC] mb-3">Trusted By Leaders</p>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Strategic Customers</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Serving India's leading enterprises across banking, insurance, manufacturing, and technology sectors.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {strategicCustomers.map((customer, index) => (
                <motion.div
                  key={customer.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="rounded-xl p-5 flex flex-col items-center justify-center hover:shadow-lg transition-shadow min-h-[120px]"
                >
                  {customer.logo ? (
                    <img 
                      src={customer.logo} 
                      alt={customer.name}
                      className="h-12 w-auto object-contain mb-2"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`${customer.logo ? 'hidden' : 'flex'} w-12 h-12 rounded-xl bg-gradient-to-br from-[#0066CC] to-[#004499] items-center justify-center mb-2 shadow-md`}>
                    <span className="text-white font-bold text-sm">{customer.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}</span>
                  </div>
                  <span className="text-xs font-medium text-gray-700 text-center">{customer.name}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-xs font-semibold tracking-widest uppercase text-[#0066CC] mb-3">Why Felix Infotech</p>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  22+ Years of IT
                  <br />
                  <span className="text-[#0066CC]">Transformation Excellence</span>
                </h2>
                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                  With over two decades of expertise serving enterprises across India, Dubai, and Singapore, 
                  Felix Infotech has established itself as a trusted technology partner. Our team of 100+ 
                  certified professionals delivers customized solutions that drive business growth and innovation.
                </p>

                <div className="space-y-4">
                  {[
                    'ISO 9001 & ISO 27001 Certified Organization',
                    '100+ ITSM Implementation Engineers',
                    '25+ Cybersecurity Certified Professionals',
                    '20+ Security Solution Partners',
                    'Consistent ₹100 Cr+ Revenue for Past 3 Years',
                  ].map((item, index) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle2 className="w-5 h-5 text-[#0066CC] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8">
                  <Link 
                    to="/about" 
                    className="inline-flex items-center px-6 py-3 bg-[#0066CC] text-white font-medium rounded-full hover:bg-[#0055AA] transition-colors"
                  >
                    Learn More About Us
                    <ArrowRight className="w-4 h-4 ml-2 text-white" />
                  </Link>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-4"
              >
                {[
                  { icon: Monitor, title: 'Compute Solutions', desc: 'Servers, storage, cloud, and hyper-converged infrastructure' },
                  { icon: Shield, title: 'Network & Security', desc: 'NGFW, SIEM, SOC, EDR/XDR, and compliance solutions' },
                  { icon: Server, title: '24/7 Monitoring', desc: 'NOC, SOC, and comprehensive infrastructure monitoring' },
                  { icon: Headphones, title: 'Expert Services', desc: 'VAPT, compliance audits, training, and managed services' },
                ].map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#0066CC]/10 flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-[#0066CC]" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Alliance Partners */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Our Technology Alliances</h3>
              <p className="text-gray-600">
                Strategic partnerships with 40+ global technology leaders
              </p>
            </motion.div>

            <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
              {partners.filter(p => p.logo).slice(0, 16).map((partner, index) => (
                <motion.div
                  key={partner.name}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.03 }}
                  className="rounded-lg p-4 flex flex-col items-center justify-center hover:shadow-md transition-shadow min-h-[80px]"
                >
                  <img 
                    src={partner.logo} 
                    alt={partner.name}
                    className="h-8 w-auto object-contain grayscale hover:grayscale-0 transition-all"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span className="text-xs font-medium text-gray-500 text-center mt-1 hidden">{partner.name}</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-8"
            >
              <Link 
                to="/alliances" 
                className="inline-flex items-center gap-1 text-[#0066CC] font-medium hover:underline"
              >
                View All Partners & Certifications <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-[#0066CC] mb-3">FAQs</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Find answers to common questions about our IT solutions and services.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto">
              {faqs.slice(0, 5).map((faq, index) => (
                <FAQItem
                  key={index}
                  faq={faq}
                  isOpen={openFAQ === index}
                  toggle={() => setOpenFAQ(openFAQ === index ? null : index)}
                  index={index}
                />
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-10"
            >
              <Link 
                to="/faq" 
                className="inline-flex items-center gap-1 text-[#0066CC] font-medium hover:underline"
              >
                View All FAQs <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Contact Section */}
        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default HomePage;
